/**
 * LPC-10289: Custom hook for C2DS field error handling
 *
 * Provides C2DS error state and auto-clear functionality for form fields.
 * When a field value changes, the corresponding C2DS error is automatically cleared.
 */

import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { C2DSFieldKey } from "@investorRequest/models";
import { c2dsErrorsSelector } from "@investorRequest/store/selectors";
import { clearC2DSFieldError } from "@investorRequest/store/ducks";
import { getC2DSErrorDisplayMessage } from "@investorRequest/utils/c2dsErrorParser";

/**
 * Hook to get all C2DS errors at once (useful for components that handle multiple fields)
 */
export function useC2DSErrors() {
  const dispatch = useAppDispatch();
  const c2dsErrors = useAppSelector(c2dsErrorsSelector);

  const clearFieldError = useCallback(
    (fieldKey: C2DSFieldKey) => {
      dispatch(clearC2DSFieldError(fieldKey));
    },
    [dispatch]
  );

  // LPC-10289: Direct function calls without useCallback to ensure fresh values
  const getFieldError = (fieldKey: C2DSFieldKey) => {
    if (!c2dsErrors) return null;

    return c2dsErrors[fieldKey];
  };

  const getFieldErrorMessage = (fieldKey: C2DSFieldKey) => {
    if (!c2dsErrors) return "";

    return getC2DSErrorDisplayMessage(c2dsErrors[fieldKey]);
  };

  const hasFieldError = (fieldKey: C2DSFieldKey) => {
    if (!c2dsErrors) return false;

    return c2dsErrors[fieldKey] !== null;
  };

  return {
    c2dsErrors,
    clearFieldError,
    getFieldError,
    getFieldErrorMessage,
    hasFieldError,
  };
}
